import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const DocumentChecklistScroll = () => {
    const sectionRef = useRef(null);
    const cardsRef = useRef([]);
    const titleRef = useRef(null);

    const categories = [
        {
            title: "Academic Documents",
            icon: "fa-book",
            docs: ["Mark Sheets (10th, 12th, Graduation)", "Degree Certificates", "Transcripts", "Course Completion Certificate"]
        },
        {
            title: "Financial Documents",
            icon: "fa-money-check",
            docs: ["Bank Statements (6 months)", "Income Tax Returns", "Salary Certificates", "Education Loan Documents"]
        },
        {
            title: "Identity Documents",
            icon: "fa-id-card",
            docs: ["Valid Passport", "Passport Size Photos", "Aadhar Card", "Birth Certificate"]
        },
        {
            title: "English Proficiency",
            icon: "fa-language",
            docs: ["IELTS Score Card", "TOEFL/PTE Score", "English Medium Certificate", "SOP/Statement of Purpose"]
        }
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation with scale effect
            gsap.fromTo(
                titleRef.current,
                {
                    opacity: 0,
                    y: 50,
                    scale: 0.9,
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 50%',
                        scrub: 1,
                    },
                }
            );

            // Cards staggered animation with 3D rotation
            cardsRef.current.forEach((card, index) => {
                gsap.fromTo(
                    card,
                    {
                        opacity: 0,
                        y: 100,
                        scale: 0.8,
                        rotateX: index % 2 === 0 ? -20 : 20,
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        rotateX: 0,
                        duration: 1,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: card,
                            start: 'top 85%',
                            end: 'top 50%',
                            scrub: 1,
                        },
                    }
                );
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="documents"
            className="py-20 bg-white relative overflow-hidden"
        >
            {/* Decorative background elements */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-primary-red/5 rounded-full -translate-y-1/2 -translate-x-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-red/5 rounded-full translate-y-1/2 translate-x-1/2"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header with kinetic typography */}
                <div ref={titleRef} className="text-center mb-16">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <i className="fas fa-clipboard-check text-primary-red text-2xl"></i>
                        <h2 className="section-title">Document Checklist</h2>
                    </div>
                    <p className="section-subtitle">Essential documents for your visa application</p>
                </div>

                {/* Document Categories Grid with 3D animations */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
                    {categories.map((category, index) => (
                        <div
                            key={index}
                            ref={(el) => (cardsRef.current[index] = el)}
                            className="doc-category bg-lightGray p-6 rounded-xl hover:shadow-xl transition-all duration-500 group"
                        >
                            <div className="w-14 h-14 bg-primary-red rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500">
                                <i className={`fas ${category.icon} text-white text-2xl`}></i>
                            </div>
                            <h3 className="text-lg font-semibold text-black mb-4">{category.title}</h3>
                            <ul className="space-y-3">
                                {category.docs.map((doc, docIndex) => (
                                    <li key={docIndex} className="flex items-center gap-2 text-sm text-gray">
                                        <i className="fas fa-check-circle text-primary-red"></i>
                                        {doc}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-10">
                    <button className="btn-primary inline-flex items-center gap-2">
                        <i className="fas fa-download"></i>
                        Download Complete Checklist
                    </button>
                </div>
            </div>
        </section>
    );
};

export default DocumentChecklistScroll;
